import { useState, useMemo, useEffect } from "react";
import { Link } from "react-router-dom";
import {
  ArrowUpRight,
  Trophy,
  TrendingDown,
  ShieldAlert,
  Rocket,
  Calculator,
} from "lucide-react";
import { AutoLoanLenderTable } from "./tables";
import CompactRubricStrip from "./CompactRubricStrip";
import PortalTooltip from "./PortalTooltip";
import { API } from "../lib/api";
import {
  AUTO_LOAN_RUBRIC,
  AUTO_LOAN_RUBRIC_CONTEXT,
} from "../lib/categoryRubrics";

/**
 * Homepage "rate comparison" section.
 *
 * Wraps the production PartnerRateTable in an editorial-grade fintech
 * wrapper. Renders the AUTO LOAN rate table (the insurance table lives
 * directly above this on Home — by here, the visitor's intent has
 * shifted to financing).
 *
 * Iter-196.QQ — Conversion optimization pass.
 *   - Methodology block compressed from a 4-card ~320px grid into a
 *     compact 1-line scoring summary (~80px) with a "View full
 *     methodology" disclosure that expands the per-factor explainers
 *     + editorial context on demand. Same SEO + structured-data
 *     surface; same iter-147 test contracts.
 *   - NEW: Loan-purpose filter chips (New Car · Used Car · Refinance ·
 *     Bad Credit) above the table. Default "All" preserves the
 *     legacy view. Selecting a chip filters the table client-side
 *     (re-running the existing AutoLoanLenderTable engine with
 *     `category="refinance"` or `creditMax`).
 *   - NEW: Top-3 award badges row (Best Overall · Lowest APR · Best
 *     for Bad Credit · Fastest Funding) computed from the live lender
 *     dataset. Each badge clarifies WHO wins each axis before the
 *     user even scans the table, and the chips link directly to the
 *     winning lender row.
 *   - NEW: Estimated monthly payment hover-tooltip on the badge row
 *     shows the $30k / 60-mo payment math so the user understands the
 *     headline APR in $$ terms before scrolling further.
 *
 * Iter-238 — AUTO_LOAN_RUBRIC + AUTO_LOAN_RUBRIC_CONTEXT lifted into
 * `lib/categoryRubrics.jsx` so the /auto-loans landing page can render
 * the same strip from one source of truth (mirrors iter-237 insurance
 * registry).
 */

// Standardized assumptions for monthly-payment math.
// Industry-standard benchmark used by Bankrate / NerdWallet / Edmunds
// when comparing lender APRs apples-to-apples.
const PMT_LOAN_AMOUNT = 30000;
const PMT_TERM_MONTHS = 60;

// Iter-196.QQ — Loan-purpose filter chips. Each chip maps to an
// existing query path (no backend changes required):
//   • new / used → keeps category="auto-loan" but switches the
//     subtitle copy. The current lender catalog covers both segments.
//   • refinance → swaps the underlying table to category="refinance"
//     (separate dataset, same rendering engine).
//   • bad-credit → keeps category="auto-loan" but caps creditMin so
//     only sub-600 floor lenders render.
const PURPOSE_FILTERS = [
  { id: "all", label: "All lenders", testid: "purpose-filter-all" },
  { id: "new", label: "New Car", testid: "purpose-filter-new" },
  { id: "used", label: "Used Car", testid: "purpose-filter-used" },
  { id: "refinance", label: "Refinance", testid: "purpose-filter-refinance" },
  { id: "bad-credit", label: "Bad Credit", testid: "purpose-filter-bad-credit" },
];

function calcMonthlyPayment(aprPercent, loan = PMT_LOAN_AMOUNT, term = PMT_TERM_MONTHS) {
  const r = (Number(aprPercent) || 0) / 100 / 12;
  if (r === 0) return loan / term;
  const factor = Math.pow(1 + r, term);
  return (loan * (r * factor)) / (factor - 1);
}

export default function ComparisonTable() {
  const [purpose, setPurpose] = useState("all");
  const [lenders, setLenders] = useState([]);

  // Map filter chip → table props. `refinance` switches the category
  // wholesale; `bad-credit` shrinks creditMin to ≤580 lenders.
  const tableProps = useMemo(() => {
    if (purpose === "refinance")
      return { category: "refinance", creditMin: undefined, creditMax: undefined };
    if (purpose === "bad-credit")
      return { category: "auto-loan", creditMax: 580 };
    return { category: "auto-loan" };
  }, [purpose]);

  // Pull the same lender feed that the table uses so the badges row
  // sees the same dataset. Single fetch, no waterfall, no state-sync
  // bugs because the table maintains its own internal state.
  useEffect(() => {
    let active = true;
    const cat = tableProps.category;
    fetch(`${API}/lender-rates?category=${encodeURIComponent(cat)}&status=published`)
      .then((r) => (r.ok ? r.json() : []))
      .then((items) => {
        if (!active) return;
        setLenders(Array.isArray(items) ? items : []);
      })
      .catch(() => active && setLenders([]));
    return () => {
      active = false;
    };
  }, [tableProps.category]);

  // Compute the 4 award badges from the live dataset. Each badge is
  // null if the dataset can't produce a meaningful winner (small lists).
  const awards = useMemo(() => computeAwards(lenders), [lenders]);

  return (
    <section
      data-testid="comparison-table-section"
      className="bg-white py-20 lg:py-24 border-y border-slate-200"
    >
      <div className="max-w-wide mx-auto px-6 lg:px-10">
        {/* Editorial header */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 mb-6 items-end">
          <div className="lg:col-span-8">
            <p className="text-xs font-bold uppercase tracking-[0.25em] text-[#10B981] mb-3">
              How CarSavr ranks auto lenders
            </p>
            <h2 className="font-display font-black text-4xl md:text-5xl lg:text-6xl tracking-tighter leading-[0.95] text-slate-950 max-w-2xl">
              Compare auto loans side-by-side.
            </h2>
            <p className="mt-4 text-base md:text-lg text-slate-600 leading-relaxed max-w-2xl">
              Every major U.S. lender scored on the same 4-factor rubric. Sort
              by lowest APR, highest editor score, or lowest credit floor.
            </p>
          </div>
          <div className="lg:col-span-4 lg:text-right">
            <Link
              to="/compare"
              data-testid="comparison-table-see-all"
              className="inline-flex items-center gap-2 font-bold text-slate-950 border-b border-slate-950 pb-1 hover:gap-3 transition-all"
            >
              See all comparisons
              <ArrowUpRight className="w-4 h-4" />
            </Link>
          </div>
        </div>

        {/* Compact 4-factor methodology — same SEO surface, ~75% less
            vertical space. */}
        <CompactRubricStrip
          testid="comparison-table-methodology"
          eyebrow="Our rubric · 4-factor lender score"
          rubricHeaderTestid="comparison-table-rubric-header"
          contextTestid="comparison-table-rubric-context"
          factors={AUTO_LOAN_RUBRIC}
          contextCopy={AUTO_LOAN_RUBRIC_CONTEXT}
          sourcesLine="Updated quarterly · sources cited per lender review"
        />

        {/* Loan-purpose filter chips + award badges row. */}
        <PurposeFilterChips value={purpose} onChange={setPurpose} />

        {awards.length > 0 && (
          <AwardBadgeRow awards={awards} />
        )}

        <AutoLoanLenderTable
          {...tableProps}
          sourcePage="home-comparison-table"
          title={titleForPurpose(purpose)}
          subtitle={subtitleForPurpose(purpose)}
          limit={6}
          compactHeader
        />
      </div>
    </section>
  );
}

// ─── Loan-purpose filter chip strip ───────────────────────────────
function PurposeFilterChips({ value, onChange }) {
  return (
    <div
      data-testid="auto-loan-purpose-filters"
      role="radiogroup"
      aria-label="Filter lenders by loan purpose"
      className="flex flex-wrap items-center gap-2 mb-4"
    >
      <span className="text-[11px] font-bold uppercase tracking-[0.18em] text-slate-500 mr-1">
        Filter by purpose
      </span>
      {PURPOSE_FILTERS.map((opt) => {
        const active = value === opt.id;
        return (
          <button
            key={opt.id}
            type="button"
            role="radio"
            aria-checked={active}
            onClick={() => onChange(opt.id)}
            data-testid={opt.testid}
            className={`h-9 px-3 text-[12px] font-bold uppercase tracking-[0.1em] border transition-colors ${
              active
                ? "bg-slate-950 text-white border-slate-950"
                : "bg-white text-slate-700 border-slate-300 hover:border-slate-950"
            }`}
          >
            {opt.label}
          </button>
        );
      })}
    </div>
  );
}

// ─── Top-3 award badge row ────────────────────────────────────────
function AwardBadgeRow({ awards }) {
  return (
    <div
      data-testid="auto-loan-award-badges"
      aria-label="Top picks across key axes"
      className="grid grid-cols-2 lg:grid-cols-4 gap-2 lg:gap-3 mb-6"
    >
      {awards.map((a) => {
        const Icon = a.Icon;
        return (
          <div
            key={a.id}
            data-testid={`award-badge-${a.id}`}
            className="border border-slate-200 bg-white p-3 flex items-start gap-3 hover:border-slate-950 transition-colors"
          >
            <span
              className={`w-8 h-8 grid place-items-center shrink-0 ${a.iconBg}`}
            >
              <Icon className={`w-4 h-4 ${a.iconColor}`} strokeWidth={2.25} />
            </span>
            <div className="min-w-0 flex-1">
              <p className="text-[9px] font-black uppercase tracking-[0.18em] text-[#10B981]">
                {a.label}
              </p>
              <p className="text-sm font-display font-black text-slate-950 leading-tight truncate mt-0.5">
                {a.winner.name}
              </p>
              <div className="flex items-center gap-2 mt-1">
                <p className="text-[11px] text-slate-600 leading-snug">
                  {a.detail}
                </p>
                {a.winner.min_apr != null && (
                  <PortalTooltip
                    testid={`award-${a.id}-monthly-payment-trigger`}
                    ariaLabel={`Estimated monthly payment for ${a.winner.name}`}
                    width={272}
                    triggerClassName="inline-flex items-center justify-center w-4 h-4 text-slate-500 hover:text-[#10B981] transition-colors shrink-0"
                    trigger={
                      <Calculator className="w-3.5 h-3.5" strokeWidth={2.25} />
                    }
                    panel={
                      <>
                        <span className="block text-[10px] font-black uppercase tracking-[0.18em] text-[#10B981] mb-2">
                          Est. monthly payment
                        </span>
                        <span className="block text-2xl font-display font-black tabular-nums">
                          ${Math.round(calcMonthlyPayment(a.winner.min_apr)).toLocaleString()}
                          <span className="text-xs text-slate-500 font-normal">
                            {" "}
                            /mo
                          </span>
                        </span>
                        <span className="block text-[11px] text-slate-300 leading-snug mt-2">
                          ${(PMT_LOAN_AMOUNT).toLocaleString()} loan · {PMT_TERM_MONTHS}-month term · {a.winner.min_apr.toFixed(2)}% APR (starting rate at qualifying credit). Your rate may differ based on credit, term, and vehicle.
                        </span>
                      </>
                    }
                  />
                )}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

// Award computation — pure function, no React state. Inputs: array of
// lender_rates rows (auto-loan or refinance). Returns up to 4 award
// objects ordered Best Overall → Lowest APR → Bad Credit → Fastest
// Funding. Null winners are filtered out so we never render an empty
// badge tile.
function fundingDaysOf(l) {
  // Per the LenderRate schema (backend/models/__init__.py:432),
  // funding speed lives inside the free-shaped `details` dict under
  // `funding_speed_days`. Older seeds may have used a flatter
  // `funding_days` key — accept both to be forgiving.
  if (typeof l.funding_days === "number") return l.funding_days;
  if (l.details && typeof l.details.funding_speed_days === "number")
    return l.details.funding_speed_days;
  return null;
}

function computeAwards(lenders) {
  if (!lenders || lenders.length === 0) return [];
  const ratedByEditor = lenders
    .filter((l) => typeof l.rating === "number")
    .sort((a, b) => (b.rating || 0) - (a.rating || 0));
  const byApr = lenders
    .filter((l) => typeof l.min_apr === "number")
    .sort((a, b) => a.min_apr - b.min_apr);
  const badCreditFloor = lenders
    .filter((l) => typeof l.min_credit === "number" && l.min_credit <= 600)
    .sort((a, b) => a.min_credit - b.min_credit);
  const fastest = lenders
    .map((l) => ({ l, d: fundingDaysOf(l) }))
    .filter((x) => typeof x.d === "number")
    .sort((a, b) => a.d - b.d)
    .map((x) => ({ ...x.l, funding_days: x.d }));

  const out = [];
  if (ratedByEditor[0]) {
    out.push({
      id: "best-overall",
      label: "Best Overall",
      Icon: Trophy,
      iconBg: "bg-amber-100",
      iconColor: "text-amber-700",
      winner: ratedByEditor[0],
      detail: `${ratedByEditor[0].rating.toFixed(1)}/5 editor score · ${ratedByEditor[0].min_apr?.toFixed(2) ?? "—"}% APR`,
    });
  }
  if (byApr[0]) {
    out.push({
      id: "lowest-apr",
      label: "Lowest APR",
      Icon: TrendingDown,
      iconBg: "bg-emerald-100",
      iconColor: "text-emerald-700",
      winner: byApr[0],
      detail: `From ${byApr[0].min_apr.toFixed(2)}% APR · min credit ${byApr[0].min_credit ?? "—"}`,
    });
  }
  if (badCreditFloor[0]) {
    out.push({
      id: "best-for-bad-credit",
      label: "Best for Bad Credit",
      Icon: ShieldAlert,
      iconBg: "bg-sky-100",
      iconColor: "text-sky-700",
      winner: badCreditFloor[0],
      detail: `Min credit ${badCreditFloor[0].min_credit}+ · from ${badCreditFloor[0].min_apr?.toFixed(2) ?? "—"}% APR`,
    });
  }
  if (fastest[0]) {
    out.push({
      id: "fastest-funding",
      label: "Fastest Funding",
      Icon: Rocket,
      iconBg: "bg-violet-100",
      iconColor: "text-violet-700",
      winner: fastest[0],
      detail: `${fastest[0].funding_days}-day funding · from ${fastest[0].min_apr?.toFixed(2) ?? "—"}% APR`,
    });
  }
  return out;
}

function titleForPurpose(p) {
  switch (p) {
    case "new":
      return "Top new-car loan lenders we've audited";
    case "used":
      return "Top used-car loan lenders we've audited";
    case "refinance":
      return "Top auto refinance lenders we've audited";
    case "bad-credit":
      return "Top auto lenders for bad credit (≤580)";
    default:
      return "Top auto loan lenders we've audited";
  }
}

function subtitleForPurpose(p) {
  switch (p) {
    case "new":
      return "Editor-ranked lenders that publish a competitive new-car APR. Soft-pull pre-qualification available with most — checking won't ding your score.";
    case "used":
      return "Editor-ranked lenders that publish a competitive used-car APR (typically 0.5–1.5% higher than new-car APRs to account for valuation risk).";
    case "refinance":
      return "Editor-ranked refinance lenders. Refi when your FICO has risen 50+ points OR market APRs have dropped 1pt+ since you signed.";
    case "bad-credit":
      return "Editor-ranked lenders that publish a credit floor at or below 580 — explicitly subprime-friendly.";
    default:
      return "Editor-ranked from the five U.S. auto lenders + marketplaces we currently audit. Most let you pre-qualify with a soft credit pull — checking your real APR won't ding your score.";
  }
}
